import React, { useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  scoutApi, ScoutExitReview,
  ScoutInstrument, INSTRUMENT_DISPLAY,
} from '../../../services/api';
import { formatDateTimeUTC6 } from '../../../utils/formatters';

const decisionClass = (d: string): string => {
  switch (d) {
    case 'HOLD':      return 'exit-decision-hold';
    case 'ARMED':     return 'exit-decision-armed';
    case 'CLOSE':
    case 'CLOSE_NOW': return 'exit-decision-close';
    default:          return '';
  }
};

type Scope = 'today' | 'week' | 'all';
type InstrumentFilter = ScoutInstrument | 'ALL';

interface Props {
  /** Filter state owned by the parent page (LLM dashboard) so KPI cards + this
   * list stay in sync. Defaults make the component usable standalone too. */
  scope?: Scope;
  instrumentFilter?: InstrumentFilter;
}

/** Latest exit-strategist review per trade. Reviews-list-only — the KPI cards
 * and scope/instrument filter UI now live in the parent LLM page above this. */
const ScoutExitReviews: React.FC<Props> = ({
  scope = 'today',
  instrumentFilter = 'ALL',
}) => {
  const [reviews, setReviews] = useState<ScoutExitReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReviews(await scoutApi.getExitReviews(100, true, instrumentFilter, scope));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, [scope, instrumentFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  // Dedupe to one card per instrument (the most recent review for each).
  // Backend returns latest-per-trade ordered by reviewed_at DESC, so the
  // first one we see per instrument is automatically the freshest trade.
  const reviewsPerInstrument: ScoutExitReview[] = (() => {
    const seen = new Set<string>();
    const out: ScoutExitReview[] = [];
    for (const r of reviews) {
      const key = r.instrument ?? 'UNKNOWN';
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r);
    }
    return out;
  })();

  return (
    <div className="agent-signals-section">
      <div className="agent-signals-header" style={{ marginTop: '0.5rem' }}>
        <span className="agent-signals-stats">
          <span className="agent-stat-total">
            {reviewsPerInstrument.length} {reviewsPerInstrument.length === 1 ? 'instrument' : 'instruments'} (latest analysis per instrument)
          </span>
        </span>
        <button className="reviews-refresh-btn" onClick={fetch} disabled={loading}>
          {loading ? '...' : '↻ Refresh'}
        </button>
      </div>

      {error && <div className="advisor-error"><strong>Error:</strong> {error}</div>}

      {reviewsPerInstrument.length === 0 && !loading && (
        <p className="agent-signals-empty">
          No exit-strategist reviews for this filter. The strategist writes a review every fire
          when a position is open. This view shows the LATEST review per instrument.
        </p>
      )}

      <div className="triggers-list">
        {reviewsPerInstrument.map(r => {
          const expanded = expandedId === r.review_id;
          return (
            <div key={r.review_id} className={`trigger-card ${decisionClass(r.decision)}`}>
              <div
                className="trigger-card-header"
                onClick={() => setExpandedId(expanded ? null : r.review_id)}
                role="button"
              >
                <div className="trigger-card-title-row">
                  <span className={`exit-decision-chip ${decisionClass(r.decision)}`}>
                    {r.decision}
                  </span>
                  {r.instrument && (
                    <span className="instrument-badge" title={r.instrument}>
                      {INSTRUMENT_DISPLAY[r.instrument]}
                    </span>
                  )}
                  <strong className="trigger-name">
                    {r.direction ?? 'Trade'} @ {r.current_price != null ? `$${r.current_price.toFixed(4)}` : 'unknown px'}
                  </strong>
                  <span className="trigger-meta">
                    held {r.bars_held != null ? `${r.bars_held}h` : 'n/a'}
                  </span>
                  {r.unrealized_pl != null && (
                    <span className={`trigger-meta ${r.unrealized_pl >= 0 ? 'pl-positive' : 'pl-negative'}`}>
                      P/L {r.unrealized_pl >= 0 ? '+' : ''}{r.unrealized_pl.toFixed(2)}
                    </span>
                  )}
                  {r.fail_open && (
                    <span className="trigger-meta" style={{ color: '#f1c40f' }}>fail-open</span>
                  )}
                </div>
                <div className="trigger-card-meta-row">
                  {r.decision_confidence != null && (
                    <span className="trigger-meta">conf {(r.decision_confidence * 100).toFixed(0)}%</span>
                  )}
                  {r.latency_ms != null && (
                    <span className="trigger-meta">{(r.latency_ms / 1000).toFixed(0)}s</span>
                  )}
                  {r.tools_used != null && (
                    <span className="trigger-meta">{r.tools_used} tools</span>
                  )}
                  <span className="trigger-meta trigger-meta-dim">
                    {formatDateTimeUTC6(r.reviewed_at)}
                  </span>
                  <span className="trigger-card-chevron">{expanded ? '▾' : '▸'}</span>
                </div>
              </div>

              {expanded && (
                <div className="trigger-card-body">
                  {r.plain_english_summary && (
                    <div className="trigger-section">
                      <div className="trigger-section-title">Plain English</div>
                      <div className="advisor-markdown">
                        <ReactMarkdown>{r.plain_english_summary}</ReactMarkdown>
                      </div>
                    </div>
                  )}
                  {r.reasoning && (
                    <div className="trigger-section">
                      <div className="trigger-section-title">Technical reasoning</div>
                      <div className="trigger-reasoning">{r.reasoning}</div>
                    </div>
                  )}
                  <div className="trigger-section">
                    <div className="trigger-meta trigger-meta-dim">
                      review_id: <code>{r.review_id}</code> · trade_id: <code>{r.trade_id}</code>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScoutExitReviews;
